import emptyBeach from '@/assets/images/empty-beach.png';
import emptyField2 from '@/assets/images/empty-field2.png';
import { constants } from '@/config';
import { BRAND } from '@/models/auth/permissions.enum';

export const CHANNELS = Object.freeze({
  INSTAGRAM: 'INSTAGRAM',
  INSTAGRAM_UGC: 'INSTAGRAM_UGC',
  FACEBOOK: 'FACEBOOK',
  X: 'X',
  PINTEREST: 'PINTEREST',
  YOUTUBE: 'YOUTUBE',
  LINKEDIN: 'LINKEDIN',
  TIKTOK: 'TIKTOK',
  TIKTOK_UGC: 'TIKTOK_UGC',
  THREADS: 'THREADS',
});

export const OWNED_CHANNELS = [
  CHANNELS.INSTAGRAM,
  CHANNELS.FACEBOOK,
  CHANNELS.X,
  CHANNELS.PINTEREST,
  CHANNELS.YOUTUBE,
  CHANNELS.LINKEDIN,
  CHANNELS.TIKTOK,
  CHANNELS.THREADS,
];

export const OWNED_CHANNELS_V2 = [
  CHANNELS.INSTAGRAM,
  CHANNELS.TIKTOK,
  CHANNELS.FACEBOOK,
  CHANNELS.PINTEREST,
  CHANNELS.YOUTUBE,
  CHANNELS.LINKEDIN,
  CHANNELS.THREADS,
  CHANNELS.X,
];
export const CHANNEL_TO_SUPPORTED_BOARD = {
  [CHANNELS.INSTAGRAM]: constants.INSTAGRAM,
  [CHANNELS.INSTAGRAM_UGC]: constants.INSTAGRAM,
  [constants.INSTAGRAM_STORY]: constants.INSTAGRAM_STORY,
  [CHANNELS.FACEBOOK]: constants.FACEBOOK,
  [CHANNELS.X]: constants.TWITTER,
  [CHANNELS.PINTEREST]: constants.PINTEREST,
  [CHANNELS.TIKTOK]: constants.TIKTOK,
  [CHANNELS.TIKTOK_UGC]: constants.TIKTOK,
  [CHANNELS.YOUTUBE]: constants.YOUTUBE,
  [CHANNELS.LINKEDIN]: constants.LINKEDIN,
  [CHANNELS.THREADS]: constants.THREADS,
};

export const CHANNEL_TO_BOARD_PERMISSION = {
  [CHANNELS.INSTAGRAM]: BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM,
  [CHANNELS.INSTAGRAM_UGC]: BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM,
  [constants.INSTAGRAM_STORY]: BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM,
  [CHANNELS.FACEBOOK]: BRAND.FACEBOOK.CAN_ACCESS_FB_INSIGHTS,
  [CHANNELS.X]: BRAND.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS,
  [CHANNELS.PINTEREST]: BRAND.PINTEREST.CAN_ACCESS_PINT,
  [CHANNELS.TIKTOK]: BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS,
  [CHANNELS.TIKTOK_UGC]: BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS,
  [CHANNELS.YOUTUBE]: BRAND.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS,
  [CHANNELS.LINKEDIN]: BRAND.LINKEDIN.CAN_ACCESS_LINKEDIN_INSIGHTS,
  [CHANNELS.THREADS]: BRAND.THREADS.CAN_ACCESS_THREADS_INSIGHTS,
};

export const UGC_CHANNELS = [CHANNELS.INSTAGRAM_UGC, CHANNELS.TIKTOK_UGC];

export const CHANNEL_ICON_NAME = Object.freeze({
  [CHANNELS.INSTAGRAM]: 'instagram',
  [CHANNELS.INSTAGRAM_UGC]: 'instagram',
  [CHANNELS.FACEBOOK]: 'facebook',
  [CHANNELS.X]: 'twitter',
  [CHANNELS.PINTEREST]: 'pinterest',
  [CHANNELS.YOUTUBE]: 'youtube',
  [CHANNELS.LINKEDIN]: 'linkedin',
  [CHANNELS.TIKTOK]: 'tiktok',
  [CHANNELS.TIKTOK_UGC]: 'tiktok',
  [CHANNELS.THREADS]: 'threads',
});

export const CHANNEL_LABEL = Object.freeze({
  [CHANNELS.INSTAGRAM]: 'Instagram',
  [CHANNELS.INSTAGRAM_UGC]: 'Instagram UGC',
  [CHANNELS.FACEBOOK]: 'Facebook',
  [CHANNELS.X]: 'X',
  [CHANNELS.PINTEREST]: 'Pinterest',
  [CHANNELS.YOUTUBE]: 'YouTube',
  [CHANNELS.LINKEDIN]: 'LinkedIn',
  [CHANNELS.TIKTOK]: 'TikTok',
  [CHANNELS.TIKTOK_UGC]: 'TikTok UGC',
  [CHANNELS.THREADS]: 'Threads',
});

export const CHANNEL_SECTION_TOOLTIP = 'Applies to all media types for the selected channels';

export function getChannelColorIcon(channel) {
  const label = CHANNEL_LABEL[channel] ?? channel;
  return `${label.replace(' UGC', '').toLowerCase()}-color`;
}

export const ACTION_TYPE = Object.freeze({
  CONTENT_TAGS: 'contentTags',
  GALLERIES: 'galleries',
  CAMPAIGNS: 'campaigns',
  BOARDS: 'boards',
  HIDE_UGC: 'hideUgc',
});

export const FILTER_TYPE = Object.freeze({
  KEYWORD: 'keyword',
  DATE_PUBLISHED: 'date_published',
  MEDIA_TYPE: 'media_type',
  VIDEO_DURATION: 'video_duration',
  PROMPT: 'prompt',
});

export const CONTENT_AUTOMATION_MEDIA_TYPES = Object.freeze({
  INSTAGRAM_CAROUSEL: {
    value: 'INSTAGRAM_CAROUSEL',
    label: 'Carousel',
    platform: CHANNELS.INSTAGRAM,
    channels: [CHANNELS.INSTAGRAM, CHANNELS.INSTAGRAM_UGC],
  },
  INSTAGRAM_PHOTO: {
    value: 'INSTAGRAM_PHOTO',
    label: 'Photo',
    platform: CHANNELS.INSTAGRAM,
    channels: [CHANNELS.INSTAGRAM, CHANNELS.INSTAGRAM_UGC],
  },
  INSTAGRAM_REEL: {
    value: 'INSTAGRAM_REEL',
    label: 'Reel',
    platform: CHANNELS.INSTAGRAM,
    channels: [CHANNELS.INSTAGRAM, CHANNELS.INSTAGRAM_UGC],
  },
  INSTAGRAM_VIDEO: {
    value: 'INSTAGRAM_VIDEO',
    label: 'Video',
    platform: CHANNELS.INSTAGRAM,
    channels: [CHANNELS.INSTAGRAM, CHANNELS.INSTAGRAM_UGC],
  },
  TIKTOK_PHOTO: {
    value: 'TIKTOK_PHOTO',
    label: 'Photo',
    platform: CHANNELS.TIKTOK,
    channels: [CHANNELS.TIKTOK, CHANNELS.TIKTOK_UGC],
  },
  TIKTOK_VIDEO: {
    value: 'TIKTOK_VIDEO',
    label: 'Video',
    platform: CHANNELS.TIKTOK,
    channels: [CHANNELS.TIKTOK, CHANNELS.TIKTOK_UGC],
  },
  FACEBOOK_CAROUSEL: {
    value: 'FACEBOOK_CAROUSEL',
    label: 'Carousel',
    platform: CHANNELS.FACEBOOK,
    channels: [CHANNELS.FACEBOOK],
  },
  FACEBOOK_LINK: {
    value: 'FACEBOOK_LINK',
    label: 'Link',
    platform: CHANNELS.FACEBOOK,
    channels: [CHANNELS.FACEBOOK],
  },
  FACEBOOK_PHOTO: {
    value: 'FACEBOOK_PHOTO',
    label: 'Photo',
    platform: CHANNELS.FACEBOOK,
    channels: [CHANNELS.FACEBOOK],
  },
  FACEBOOK_REEL: {
    value: 'FACEBOOK_REEL',
    label: 'Reel',
    platform: CHANNELS.FACEBOOK,
    channels: [CHANNELS.FACEBOOK],
  },
  FACEBOOK_TEXT: {
    value: 'FACEBOOK_TEXT',
    label: 'Text',
    platform: CHANNELS.FACEBOOK,
    channels: [CHANNELS.FACEBOOK],
  },
  FACEBOOK_VIDEO: {
    value: 'FACEBOOK_VIDEO',
    label: 'Video',
    platform: CHANNELS.FACEBOOK,
    channels: [CHANNELS.FACEBOOK],
  },
  PINTEREST_CAROUSEL: {
    value: 'PINTEREST_CAROUSEL',
    label: 'Carousel',
    platform: CHANNELS.PINTEREST,
    channels: [CHANNELS.PINTEREST],
  },
  PINTEREST_PHOTO: {
    value: 'PINTEREST_PHOTO',
    label: 'Photo',
    platform: CHANNELS.PINTEREST,
    channels: [CHANNELS.PINTEREST],
  },
  PINTEREST_VIDEO: {
    value: 'PINTEREST_VIDEO',
    label: 'Video',
    platform: CHANNELS.PINTEREST,
    channels: [CHANNELS.PINTEREST],
  },
  YOUTUBE_LIVE: {
    value: 'YOUTUBE_LIVE',
    label: 'Live',
    platform: CHANNELS.YOUTUBE,
    channels: [CHANNELS.YOUTUBE],
  },
  YOUTUBE_ON_DEMAND: {
    value: 'YOUTUBE_ON_DEMAND',
    label: 'On Demand',
    platform: CHANNELS.YOUTUBE,
    channels: [CHANNELS.YOUTUBE],
  },
  YOUTUBE_SHORTS: {
    value: 'YOUTUBE_SHORTS',
    label: 'Shorts',
    platform: CHANNELS.YOUTUBE,
    channels: [CHANNELS.YOUTUBE],
  },
  LINKEDIN_CAROUSEL: {
    value: 'LINKEDIN_CAROUSEL',
    label: 'Carousel',
    platform: CHANNELS.LINKEDIN,
    channels: [CHANNELS.LINKEDIN],
  },
  LINKEDIN_DOCUMENT: {
    value: 'LINKEDIN_DOCUMENT',
    label: 'Document',
    platform: CHANNELS.LINKEDIN,
    channels: [CHANNELS.LINKEDIN],
  },
  LINKEDIN_IMAGE: {
    value: 'LINKEDIN_IMAGE',
    label: 'Image',
    platform: CHANNELS.LINKEDIN,
    channels: [CHANNELS.LINKEDIN],
  },
  LINKEDIN_LINK: {
    value: 'LINKEDIN_LINK',
    label: 'Link',
    platform: CHANNELS.LINKEDIN,
    channels: [CHANNELS.LINKEDIN],
  },
  LINKEDIN_TEXT: {
    value: 'LINKEDIN_TEXT',
    label: 'Text',
    platform: CHANNELS.LINKEDIN,
    channels: [CHANNELS.LINKEDIN],
  },
  LINKEDIN_VIDEO: {
    value: 'LINKEDIN_VIDEO',
    label: 'Video',
    platform: CHANNELS.LINKEDIN,
    channels: [CHANNELS.LINKEDIN],
  },
  THREADS_AUDIO: {
    value: 'THREADS_AUDIO',
    label: 'Audio',
    platform: CHANNELS.THREADS,
    channels: [CHANNELS.THREADS],
  },
  THREADS_CAROUSEL: {
    value: 'THREADS_CAROUSEL',
    label: 'Carousel',
    platform: CHANNELS.THREADS,
    channels: [CHANNELS.THREADS],
  },
  THREADS_IMAGE: {
    value: 'THREADS_IMAGE',
    label: 'Image',
    platform: CHANNELS.THREADS,
    channels: [CHANNELS.THREADS],
  },
  THREADS_TEXT: {
    value: 'THREADS_TEXT',
    label: 'Text',
    platform: CHANNELS.THREADS,
    channels: [CHANNELS.THREADS],
  },
  THREADS_VIDEO: {
    value: 'THREADS_VIDEO',
    label: 'Video',
    platform: CHANNELS.THREADS,
    channels: [CHANNELS.THREADS],
  },
  X_CAROUSEL: {
    value: 'X_CAROUSEL',
    label: 'Carousel',
    platform: CHANNELS.X,
    channels: [CHANNELS.X],
  },
  X_LINK: {
    value: 'X_LINK',
    label: 'Link',
    platform: CHANNELS.X,
    channels: [CHANNELS.X],
  },
  X_PHOTO: {
    value: 'X_PHOTO',
    label: 'Photo',
    platform: CHANNELS.X,
    channels: [CHANNELS.X],
  },
  X_TEXT: {
    value: 'X_TEXT',
    label: 'Text',
    platform: CHANNELS.X,
    channels: [CHANNELS.X],
  },
  X_VIDEO: {
    value: 'X_VIDEO',
    label: 'Video',
    platform: CHANNELS.X,
    channels: [CHANNELS.X],
  },
});

export const VIDEO_DURATION_FILTER_PRESETS = Object.freeze([
  { label: 'Under 15 Seconds', value: { max: 15 } },
  { label: 'Between 15-30 Seconds', value: { min: 15, max: 30 } },
  { label: 'Between 30-60 Seconds', value: { min: 30, max: 60 } },
  { label: 'Between 1-3 Minutes', value: { min: 60, max: 180 } },
  { label: 'Between 3-10 Minutes', value: { min: 180, max: 600 } },
  { label: 'Between 10-30 Minutes', value: { min: 600, max: 1800 } },
  { label: 'Between 30-60 Minutes', value: { min: 1800, max: 3600 } },
  { label: 'Over 1 Hour', value: { min: 3600 } },
]);

export function hasPromptFilter(filterGroup) {
  return (filterGroup?.items ?? []).some(
    (item) => item.key === FILTER_TYPE.PROMPT || hasPromptFilter(item),
  );
}

export const FILTER_OPERATOR = Object.freeze({
  OR: 'OR',
  AND: 'AND',
});

export const RULES_BUILDER_CHIP_TYPE = Object.freeze({
  FILTER: 'FILTER',
  ACTION: 'ACTION',
});

export const noBrandAccessTooltip = 'You don’t have access to some brands in this automation.';
export const noEditContentAutomationPermissionsTooltip =
  'You don’t have access to edit content automations. Please contact your plan administrator to enable this permission.';

export const applyToExistingPostSuccessMessage =
  'Your automation has been saved. It may take a few moments to apply it to all posts.';
export const disableContentAutomationSuccessMessage = 'Your automation has been disabled.';
export const disableContentAutomationFailedMessage =
  'Failed to disable the automation. Please try again.';
export const runContentAutomationFailedMessage = 'Failed to run the automation. Please try again.';

export const deleteAutomationConfirmationTitle = 'Delete Content Automation?';
export const deleteAutomationConfirmationMessage =
  'This will permanently remove this content automation. Any content previously impacted by this automation will remain unchanged.';
export const deleteConfirmationAlias = 'Delete';
export const deleteAutomationUnauthorizedError = (contentAutomationName) =>
  `You do not have permission to delete content automation ${contentAutomationName}.`;
export const deleteAutomationNotFoundError = (contentAutomationName) =>
  `Content automation ${contentAutomationName} was not found.`;
export const deleteAutomationUnknownError = (contentAutomationName) =>
  `Unable to delete content automation ${contentAutomationName} at this time, please try again later.`;
export const deleteAutomationSuccessMessage = (contentAutomationName) =>
  `Content automation ${contentAutomationName} successfully deleted`;

export const contentAutomationListContainsErrorMessage =
  'One or more of your automations below has an error and is not running. Edit or delete this automation to resolve the issue.';

export const contentAutomationListItemContainsErrorMessage = 'This automation has errors';

export const contentAutomationActionErrorMessages = Object.freeze({
  [ACTION_TYPE.CONTENT_TAGS]:
    'This Content Tag was deleted or is not associated with any of the brands you have selected for this automation',
  [ACTION_TYPE.BOARDS]:
    'This Board was deleted or is not associated with any of the brands you have selected for this automation',
  [ACTION_TYPE.GALLERIES]:
    'This Gallery was deleted or is not associated with any of the brands you have selected for this automation',
  [ACTION_TYPE.CAMPAIGNS]:
    'This Campaign was deleted or is not associated with any of the brands you have selected for this automation',
});

export const deleteFilterGroupConfirmationTitle = 'Delete Container?';
export const deleteFilterGroupConfirmationMessage =
  'Are you sure you want to delete this container? You can’t undo this action.';
export const deleteFilterGroupConfirmationAlias = 'Delete Container';

export const CONTENT_AUTOMATION_WIZARD_NEXT_BUTTON_TOOLTIPS = [
  'You must specify which brands will have their content monitored',
  'You must specify which social channels will have their content monitored',
  'You must specify a condition and a valid action',
];

export const noAutomationsEmptyState = Object.freeze({
  image: emptyField2,
  title: "You don't have any content automations yet",
  action: 'Create Automation',
});

export const filterEmptyState = Object.freeze({
  image: emptyBeach,
  title: 'No content automations found',
  message: 'Adjust search and try again.',
});

export const errorGetContentAutomationsEmptyState = Object.freeze({
  image: emptyField2,
  title: 'Try Again',
  message:
    'There was a delay in loading your content automations. Please refresh the page and try again.',
});

export const saveAndApplyTitle = 'Save and Apply to Existing Posts';
export const saveAndApplyBody = (counted) =>
  `This will start running the automation and apply the rule to the existing ${counted} posts that match the defined criteria`;
export const saveAndApplyConfirmationAlias = 'Continue';
export const saveWithoutApplyTitle = 'Save Rule';
export const saveWithoutApplyBody =
  'This will start running the automation on new posts only. Existing posts will not be affected.';
export const saveWithoutApplyConfirmationAlias = 'Save';
export const cancelEditConfirmationTitle = 'Discard Changes?';
export const cancelEditConfirmationMessage = 'If you exit now, your changes will be discarded.';
export const cancelEditConfirmationAlias = 'Discard';

export const createContentAutomationPanelTitle = 'Create a Rule - Owned / UGC';
export const editContentAutomationPanelTitle = 'Edit Rule - Owned / UGC';
export const editContentAutomationActionErrorMessage =
  'This criteria is no longer valid. Please remove it to save this rule.';

export const selectChannelOptions = [
  {
    value: CHANNELS.FACEBOOK,
    icon: CHANNELS.FACEBOOK.toLocaleLowerCase(),
    label: 'Facebook',
  },
  {
    value: CHANNELS.INSTAGRAM,
    icon: CHANNELS.INSTAGRAM.toLocaleLowerCase(),
    label: 'Instagram',
  },
  {
    value: CHANNELS.LINKEDIN,
    icon: CHANNELS.LINKEDIN.toLocaleLowerCase(),
    label: 'LinkedIn',
  },
  {
    value: CHANNELS.PINTEREST,
    icon: CHANNELS.PINTEREST.toLocaleLowerCase(),
    label: 'Pinterest',
  },
  {
    value: CHANNELS.THREADS,
    icon: CHANNELS.THREADS.toLocaleLowerCase(),
    label: 'Threads',
  },
  {
    value: CHANNELS.TIKTOK,
    icon: CHANNELS.TIKTOK.toLocaleLowerCase(),
    label: 'TikTok',
  },
  {
    value: CHANNELS.X,
    icon: 'twitter',
    label: 'X',
  },
  {
    value: CHANNELS.YOUTUBE,
    icon: CHANNELS.YOUTUBE.toLocaleLowerCase(),
    label: 'YouTube',
  },
  {
    value: CHANNELS.INSTAGRAM_UGC,
    icon: 'instagram',
    label: 'Instagram',
    name: 'Instagram UGC',
  },
  {
    value: CHANNELS.TIKTOK_UGC,
    icon: 'tiktok',
    label: 'TikTok',
    name: 'TikTok UGC',
  },
];

export const CONTENT_AUTOMATION_SUBHEADER_MESSAGE =
  'You can create rules to add content tags to posts or add published posts to Campaigns, based on specific conditions, such as channel, captions and date published.';

export const CONTENT_AGENT_SUBHEADER_MESSAGE =
  'Your agent can add content tags to posts or add published posts to Campaigns, based on specific conditions, such as channel, captions and date published.';

export const PANEL_NAMES = Object.freeze({
  BRANDS: 'brands',
  CHANNELS: 'channels',
  RULES: 'rules',
  NAME_AND_SAVE: 'nameAndSave',
});

export const CONTENT_AUTOMATION_PAGE_SIZE = 8;

export const CONTENT_AUTOMATION_LEARN_MORE_URL =
  'https://help.dashsocial.com/hc/en-us/articles/31114279045389-Managing-Content-Automation';

export const CONTENT_AUTOMATION_EDIT_PERMISSION_REQUIRED_TOOLTIP =
  "You don't have access to edit content automations. Please contact your plan administrator to enable this permission.";

export const CONTENT_AUTOMATION_BRAND_ACCESS_REQUIRED_TOOLTIP =
  "You don't have access to some brands in this automation";

export const CONTENT_AUTOMATION_RUN_ERROR_MESSAGE =
  'Failed to run the automation. Please try again.';

export const VISION_AI_PROMPT_PRESETS = Object.freeze([
  { label: 'Create Your Own', icon: 'draft', form: { name: '', prompt: '' } },
  {
    label: 'Not Safe for Work',
    icon: 'remove-circle',
    form: {
      name: 'Not Safe for Work',
      prompt:
        'Posts that are not safe for work where post content includes profanity, slurs, explicit language, or inappropriate visual or audio references. Include content that may be offensive, sexually explicit, or unsuitable for professional environments.\n\nExclude neutral language, mild slang used non-offensively, or content that references sensitive topics in a factual or educational context.',
    },
  },
  {
    label: 'Product Launch',
    icon: 'entertainment-score',
    form: {
      name: 'Product Launch',
      prompt:
        'Posts about launching a new product, feature, or collection, including teasers, launch announcements, and first-look reactions.\n\nExclude evergreen product mentions and general brand awareness content that is not tied to a launch moment.',
    },
  },
  {
    label: 'Special Events',
    icon: 'campaign',
    form: {
      name: 'Special Events',
      prompt:
        'Posts tied to holidays, cultural moments, seasonal campaigns, sponsorships, or one-time brand events.\n\nExclude regular programming or recurring content that is not connected to a distinct event.',
    },
  },
]);

export const VISION_AI_PROMPT_FORM_HINT =
  'The prompt should include examples of what posts should include and also exclude. Preview matching posts in Step 4, then continue to refine the prompt until results are accurate.';

export const VISION_AI_PROMPT_FILTER_KEY = 'prompt';

export const MAX_VISION_AI_PROMPTS_PER_BRAND = 10;

export const VISION_AI_PROMPT_FILTER_TOOLTIP_BRAND_LIMIT =
  'One or more brands has reached the limit of the number of Vision AI Prompts';

export const VISION_AI_PROMPT_FILTER_TOOLTIP_ONE_PER_AUTOMATION =
  'Only one Vision AI Prompt can be used per automation';

export const VISION_AI_PROMPT_FILTER_TOOLTIP_DEFAULT =
  'Describe your content segment and Vision will help you categorize based on post captions, images and video qualities.';

export const VISION_AI_PROMPT_VALIDATION = Object.freeze({
  NAME_EMPTY: 'Name cannot be empty',
  NAME_MAX_LENGTH: 'Name cannot exceed 50 characters',
  PROMPT_EMPTY: 'Prompt cannot be empty',
  PROMPT_MAX_LENGTH: 'Prompt cannot exceed 1,200 characters',
});

export const MAX_MEDIA_TYPE_DISPLAY_OPTIONS = 3;
